import * as React from 'react';

import { useBreakpointValue } from '@notino/react-styleguide';
import {
  GetProductsByIdBatchedQuery,
  RoutineByProductIdQuery,
} from '@notino/shared/definitions/types';

import ProductItem from '@components/Universals/ProductItem';
import { useGetMasterAddToCart } from '@helpers/googleTagManager';

import { GTM_LIST_LABEL } from './constants';
import { RoutineHeader } from './RoutineHeader';
import { RoutineStepIndicator } from './RoutineStepIndicator';
import {
  RoutineContentWrapper,
  RoutineProducts,
  RoutineProductsWrapper,
} from './styled/RoutineContent';
import { ProductWrapper } from './styled/RoutineProduct';
import { trackImpressionWithProductList } from './tracking';

export interface IRoutineContentProps {
  routine: NonNullable<
    NonNullable<
      RoutineByProductIdQuery['RoutineByProductId']['routines']
    >[number]
  >;
}

const RoutineContent: React.FC<IRoutineContentProps> = ({
  routine: { products, ...restRoutine },
}) => {
  const filteredProducts = (products ?? []).filter(
    (p): p is NonNullable<typeof p> => Boolean(p?.product)
  );
  const isDesktop = useBreakpointValue({ md: true });

  const vpProducts = filteredProducts
    .map((p) => p.product)
    .filter((p): p is NonNullable<typeof p> => Boolean(p));

  React.useEffect(() => {
    if (vpProducts.length) {
      trackImpressionWithProductList(vpProducts);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const trackAddToCart = useGetMasterAddToCart(GTM_LIST_LABEL);

  const onProductAdded = React.useCallback(
    async (product: GetProductsByIdBatchedQuery['vpProductByIds'][number]) => {
      if (!product) return;
      await trackAddToCart(
        product as Parameters<typeof trackAddToCart>[0],
        product.catalogMasterId?.toString() ?? '',
        vpProducts.findIndex(
          (p) =>
            p && Number(p.catalogMasterId) === Number(product.catalogMasterId)
        ) + 1
      );
    },
    [vpProducts, trackAddToCart]
  );

  const productsCount = vpProducts.length;
  if (!productsCount) {
    return null;
  }

  return (
    <RoutineContentWrapper
      data-testid="routines"
      id="routines"
      productsCount={productsCount}
    >
      <RoutineHeader {...restRoutine} productsCount={productsCount} />
      <RoutineProductsWrapper>
        <RoutineProducts productsCount={productsCount}>
          {filteredProducts.map((routineProduct, index) => {
            const product = routineProduct.product;
            if (!product) {
              return null;
            }

            const description = routineProduct.description;
            return (
              <ProductWrapper key={product.id} productsCount={productsCount}>
                {!isDesktop && (
                  <RoutineStepIndicator
                    stepNumber={index + 1}
                    totalSteps={productsCount}
                  />
                )}
                <ProductItem
                  buttonElementClassName="routine-buy-button"
                  description={isDesktop ? description ?? undefined : undefined}
                  order={isDesktop ? index + 1 : undefined}
                  position={index + 1}
                  showBuyButton={true}
                  GTMEventLabel={GTM_LIST_LABEL}
                  sendGTM={true}
                  onProductAdded={onProductAdded}
                  noPriceIfUnavailable={true}
                  product={product}
                  inSectionOfType="routine"
                  wishlistActionLocation="list"
                />
              </ProductWrapper>
            );
          })}
        </RoutineProducts>
      </RoutineProductsWrapper>
    </RoutineContentWrapper>
  );
};

export default RoutineContent;
